/**
 * Paramètres d'URL qui portent un secret et ne doivent jamais partir chez un
 * tiers (PostHog, Sentry) :
 *   - `token` : lien de réinitialisation du mot de passe et de vérification
 *     d'email (JWT de 30 min, rejouable : quiconque le lit prend le compte) ;
 *   - `tradovateComplete` : ticket du flux OAuth Tradovate lié.
 * Audit sécurité 2026-10-22, H5.
 */
const SECRET_PARAMS = ["token", "tradovateComplete"];

const SECRET_PARAM_RE = new RegExp(`([?&#](?:${SECRET_PARAMS.join("|")})=)[^&#\\s"]*`, "gi");

export const REDACTED = "[redacted]";

/** Masque la valeur des paramètres secrets dans une URL (ou tout texte). */
export function redactUrlSecrets(value: string): string {
  return value.replace(SECRET_PARAM_RE, `$1${REDACTED}`);
}

/**
 * Applique `redactUrlSecrets` à toutes les chaînes d'un objet de propriétés,
 * en profondeur (PostHog range `$set_once.$initial_current_url` sous un
 * sous-objet). Rend un nouvel objet ; l'entrée n'est pas modifiée.
 */
export function redactSecretsDeep<T>(input: T, depth = 0): T {
  if (typeof input === "string") return redactUrlSecrets(input) as T;
  if (depth > 5 || input === null || typeof input !== "object") return input;
  if (Array.isArray(input)) return input.map((v) => redactSecretsDeep(v, depth + 1)) as T;
  // Seuls les objets simples sont reconstruits : une Date (timestamp d'un
  // événement) ou tout autre objet typé est rendu tel quel.
  const proto = Object.getPrototypeOf(input);
  if (proto !== Object.prototype && proto !== null) return input;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    out[k] = redactSecretsDeep(v, depth + 1);
  }
  return out as T;
}

interface PostHogEventLike {
  event?: string;
  properties?: Record<string, any>;
  $set?: Record<string, any>;
  $set_once?: Record<string, any>;
}

/**
 * `before_send` de PostHog. Les événements d'enregistrement de session
 * (`$snapshot`) sont volumineux : on n'y masque que l'URL des événements
 * « meta » (type 4), sans parcourir le DOM enregistré.
 */
export function redactPostHogEvent<T extends PostHogEventLike | null>(event: T): T {
  if (!event) return event;
  if (event.event === "$snapshot") {
    const data = event.properties?.$snapshot_data;
    if (Array.isArray(data)) {
      for (const item of data) {
        if (item?.type === 4 && typeof item.data?.href === "string") {
          item.data.href = redactUrlSecrets(item.data.href);
        }
      }
    }
    return event;
  }
  return {
    ...event,
    properties: redactSecretsDeep(event.properties),
    $set: redactSecretsDeep(event.$set),
    $set_once: redactSecretsDeep(event.$set_once),
  };
}
